import React, { useState, useRef, useEffect } from "react";
import "./Chatbot.css";
import botIcon from "./botIcon.png";

/* ═══════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════ */

const WHATSAPP = "918640091593";
const wa = (msg) =>
  `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`;

const SERVICES = [
  { id: "software", icon: "💻", title: "Software Development", desc: "Custom enterprise software solutions engineered for scalability, security & long-term performance. We transform complex business workflows into intuitive digital applications." },
  { id: "web", icon: "🌐", title: "Web Development", desc: "Modern, responsive websites & PWAs using Next.js, React & cutting-edge frameworks. Lightning-fast, SEO-optimized & pixel-perfect designs." },
  { id: "app", icon: "📱", title: "App Development", desc: "Native & cross-platform mobile apps for iOS & Android using Flutter & React Native. Smooth animations, offline support & app store deployment." },
  { id: "ai", icon: "🤖", title: "AI Solutions", desc: "AI chatbots, predictive analytics & ML models using OpenAI, NLP & Computer Vision. Automate operations & unlock data-driven insights." },
  { id: "cloud", icon: "☁️", title: "Cloud & DevOps", desc: "AWS, Azure, Docker, Kubernetes — CI/CD pipelines, containerization & cloud infrastructure with zero-downtime deployments." },
  { id: "api", icon: "🔗", title: "API Integration", desc: "Secure third-party API integrations — payment gateways, ERP, CRM & custom tools with real-time data sync." },
  { id: "marketing", icon: "📈", title: "Digital Marketing", desc: "SEO, social media campaigns & performance advertising. Data-driven strategies for maximum ROI & lead generation." },
];

const COURSES = [
  { id: "fullstack", icon: "🚀", title: "Full Stack Development", duration: "6 Months", level: "Beginner → Advanced", topics: "HTML, CSS, JavaScript, React.js, Python, Django, REST API, MySQL, MongoDB, Git, Deployment", projects: "Portfolio Website, E-Commerce Site, Auth System, Full Stack Web App" },
  { id: "data", icon: "📊", title: "Data Analytics", duration: "4 Months", level: "Beginner", topics: "Python, NumPy, Pandas, Matplotlib, Seaborn, Power BI, SQL, MySQL, Excel", projects: "Sales Data Analysis, Student Performance Dashboard, Business Dashboard" },
  { id: "appdev", icon: "📱", title: "Mobile App Development", duration: "4 Months", level: "Beginner → Advanced", topics: "Flutter, Dart, React Native, REST API, Firebase, SQLite, Mobile UI Design", projects: "Login App, QR Attendance App, E-Commerce App, Student Management App" },
  { id: "frontend", icon: "🎨", title: "Frontend Development", duration: "3 Months", level: "Beginner → Advanced", topics: "HTML5, CSS3, JavaScript ES6+, React.js, Bootstrap, Tailwind CSS, Material UI", projects: "Portfolio Website, Landing Page, React Dashboard, E-Commerce UI, Blog" },
  { id: "backend", icon: "⚙️", title: "Backend Development", duration: "5 Months", level: "Beginner → Advanced", topics: "Python, Django, DRF, Node.js, Express.js, REST API, JWT, MySQL, PostgreSQL, MongoDB, AWS", projects: "Auth System, Blog API, E-Commerce Backend, Payment Integration, Admin Panel" },
  { id: "hr", icon: "👥", title: "HR & Marketing", duration: "3 Months", level: "Beginner → Advanced", topics: "Recruitment, Employee Engagement, Payroll, Branding, Social Media Marketing, SEO Basics", projects: "Recruitment Drive Plan, Employee Handbook, Marketing Campaign, Social Media Strategy" },
  { id: "dm", icon: "📣", title: "Digital Marketing", duration: "4 Months", level: "Beginner → Advanced", topics: "SEO, Google Ads, Meta Ads, Instagram Marketing, Content Marketing, Google Analytics, Semrush", projects: "SEO Audit, Social Media Campaign, Google Ads Campaign, Email Marketing, Brand Strategy" },
  { id: "cloudeng", icon: "🌩️", title: "Cloud Engineering", duration: "5 Months", level: "Beginner → Advanced", topics: "AWS (EC2, S3, Lambda), Azure, Docker, Kubernetes, Jenkins, CI/CD, Linux, Cloud Security", projects: "Website on S3, Web App on EC2, CI/CD Pipeline, Serverless API, K8s Cluster" },
];

const TECH_SHORT = {
  Frontend: "React, Next.js, Angular, Vue, TypeScript, Tailwind",
  Backend: "Node.js, Python, Django, FastAPI, Java, Spring Boot, PHP, Laravel",
  Database: "MySQL, PostgreSQL, MongoDB, Firebase, Redis, Oracle",
  Mobile: "Flutter, React Native, Android, iOS",
  "Cloud/DevOps": "AWS, Azure, GCP, Docker, Kubernetes, Jenkins",
  Design: "Figma, Adobe XD",
  "E-Commerce": "Shopify, WordPress, WooCommerce",
};

const HOME_OPTIONS = [
  "💻 Software Development",
  "🎓 Internship Program",
  "📞 Contact Us",
  "ℹ️ About YashviTech",
];

/* ═══════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════ */

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: "bot",
      text: "Hi! 👋 Welcome to *YashviTech IT Solution Pvt. Ltd.*\n\nWe are a Software Development & Training company based in Raipur, C.G.\n\nHow can we help you today?",
    },
  ]);
  const [options, setOptions] = useState(HOME_OPTIONS);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, open, isTyping]);

  /* helpers */
  const addBot = (text, nextOpts = [], delay = 700) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages((p) => [...p, { type: "bot", text }]);
      if (nextOpts.length) setOptions(nextOpts);
    }, delay);
  };

  const addUser = (text) => {
    setMessages((p) => [...p, { type: "user", text }]);
  };

  /* MAIN ROUTER */
  const handleOption = (opt) => {
    addUser(opt);
    setOptions([]);

    const o = opt.toLowerCase();

    /* ── HOME ── */
    if (o.includes("software development")) {
      return addBot(
        "Great choice! 💻\n\nWe provide end-to-end software solutions. What would you like to know?",
        [
          "📋 View All Services",
          "💰 Pricing & Timeline",
          "🔧 Technologies We Use",
          "🛡️ Maintenance & Support",
          "📞 Talk to Expert",
          "⬅️ Back to Home",
        ]
      );
    }

    if (o.includes("internship")) {
      return addBot(
        "Awesome! 🎓\n\nWe offer industry-focused Training & Internship programs with live projects & placement support.\n\nWhat would you like to explore?",
        [
          "📚 View All Courses",
          "💰 Fees & Duration",
          "🏫 Mode & Timing",
          "📜 Certificate & Placement",
          "📝 Apply Now",
          "⬅️ Back to Home",
        ]
      );
    }

    if (o.includes("contact")) {
      return addBot(
        "📞 *Contact YashviTech*\n\n📱 Phone: +91 86400-91593\n📧 Email: info.yashvitech@gmail.com\n📍 Address: Near Disha College, Kota, Raipur, C.G.\n🕐 Office Hours: 10:00 AM – 6:00 PM (Mon-Sat)\n\nOr message us directly on WhatsApp!",
        ["💬 Open WhatsApp", "📍 Get Directions", "⬅️ Back to Home"]
      );
    }

    if (o.includes("about")) {
      return addBot(
        "ℹ️ *About YashviTech IT Solution Pvt. Ltd.*\n\n🏢 Established: 2 October 2023\n📍 Location: Raipur, Chhattisgarh\n💼 We specialize in Software Development, Web & App Development, AI Solutions, Cloud & DevOps, and Digital Marketing.\n\n🎓 We also provide industry-focused Training & Internship programs with placement assistance.\n\n🔗 *Follow us:*\n• LinkedIn: /company/yashvitech\n• Instagram: @yashvitechraipur\n• YouTube: @yashvitech\n• Facebook: Yashvitech Raipur",
        ["⬅️ Back to Home"]
      );
    }

    /* ── SOFTWARE DEV SUB ── */
    if (o.includes("view all services") || o.includes("all services")) {
      let list = "📋 *Our Services:*\n\n";
      SERVICES.forEach((s, i) => (list += `${s.icon}  ${i + 1}. *${s.title}*\n`));
      list += "\nTap any service to know more!";
      return addBot(list, [
        ...SERVICES.map((s) => `${s.icon} ${s.title}`),
        "⬅️ Back to Software Dev",
      ]);
    }

    const matchedService = SERVICES.find(
      (s) => o.includes(s.title.toLowerCase()) && !o.includes("back")
    );
    if (matchedService) {
      return addBot(
        `${matchedService.icon} *${matchedService.title}*\n\n${matchedService.desc}\n\n💰 Pricing: Custom (depends on requirements)\n⏱️ Timeline: Contact us for estimate\n\nWant a free quote? Talk to our expert!`,
        [
          "💬 Get Free Quote (WhatsApp)",
          "📋 View All Services",
          "⬅️ Back to Software Dev",
        ]
      );
    }

    if (o.includes("pricing") && o.includes("timeline")) {
      return addBot(
        "💰 *Pricing & Timeline*\n\nEvery project is unique. Custom quotes based on:\n• Project scope & complexity\n• Features & integrations\n• Design requirements\n• Timeline & urgency\n\n📌 *Rough Estimates:*\n• Basic Website: 7–15 days\n• E-Commerce Store: 15–30 days\n• Custom Web App: 30–60 days\n• Mobile App: 30–90 days\n• AI/Enterprise: 60–120 days\n\n💡 *Payment:* Flexible advance + milestone-based.\n\nShare requirements on WhatsApp for exact quote!",
        ["💬 Get Free Quote (WhatsApp)", "⬅️ Back to Software Dev"]
      );
    }

    if (o.includes("technologies")) {
      let tech = "🔧 *Technologies We Use:*\n\n";
      Object.entries(TECH_SHORT).forEach(([cat, items]) => {
        tech += `*${cat}:*\n${items}\n\n`;
      });
      tech += "We choose the best tech stack based on your project needs!";
      return addBot(tech, ["⬅️ Back to Software Dev"]);
    }

    if (o.includes("maintenance")) {
      return addBot(
        "🛡️ *Maintenance & Support*\n\n✅ Yes! We provide post-delivery maintenance & support.\n\n• Bug fixes & updates\n• Server monitoring & uptime\n• Security patches\n• Feature enhancements\n• Performance optimization\n\nWe offer flexible AMC (Annual Maintenance Contract) plans.",
        ["💬 Talk on WhatsApp", "⬅️ Back to Software Dev"]
      );
    }

    if (o.includes("talk to expert")) {
      window.open(wa("Hi YashviTech! I need a software development quote."), "_blank");
      return addBot(
        "Opening WhatsApp… 💬\nOur expert will reply within 24 hours!",
        [
          "📋 View All Services",
          "💰 Pricing & Timeline",
          "🔧 Technologies We Use",
          "🛡️ Maintenance & Support",
          "⬅️ Back to Home",
        ]
      );
    }

    if (o.includes("back to software")) {
      return addBot("Sure! What would you like to know about Software Development?", [
        "📋 View All Services",
        "💰 Pricing & Timeline",
        "🔧 Technologies We Use",
        "🛡️ Maintenance & Support",
        "📞 Talk to Expert",
        "⬅️ Back to Home",
      ]);
    }

    /* ── INTERNSHIP SUB ── */
    if (o.includes("view all courses") || o.includes("all courses")) {
      let list = "📚 *Available Courses:*\n\n";
      COURSES.forEach((c) => (list += `${c.icon}  *${c.title}* — ${c.duration}\n`));
      list += "\nTap any course for details!";
      return addBot(list, [
        ...COURSES.map((c) => `${c.icon} ${c.title}`),
        "⬅️ Back to Internship",
      ]);
    }

    const matchedCourse = COURSES.find(
      (c) => o.includes(c.title.toLowerCase()) && !o.includes("back")
    );
    if (matchedCourse) {
      return addBot(
        `${matchedCourse.icon} *${matchedCourse.title}*\n\n⏱️ Duration: ${matchedCourse.duration}\n📊 Level: ${matchedCourse.level}\n\n*📖 Topics Covered:*\n${matchedCourse.topics}\n\n*🛠️ Real Projects:*\n${matchedCourse.projects}\n\n🏫 Mode: Online / Offline / Hybrid\n🕐 Timing: 10:00 AM – 5:00 PM\n📜 Certificate: Yes (Industry-recognized)\n💼 Placement: Assistance provided\n\nReady to join? Apply now!`,
        [
          "📝 Apply for This Course",
          "📚 View All Courses",
          "⬅️ Back to Internship",
        ]
      );
    }

    if (o.includes("fees") && o.includes("duration")) {
      let table = "💰 *Fees & Duration:*\n\n";
      COURSES.forEach(
        (c) =>
          (table += `${c.icon} *${c.title}*\n⏱️ ${c.duration} | 💵 Paid\n\n`)
      );
      table += "📌 Fees vary by course. Contact us for exact pricing & EMI options!";
      return addBot(table, ["💬 Ask Fees on WhatsApp", "⬅️ Back to Internship"]);
    }

    if (o.includes("mode") && o.includes("timing")) {
      return addBot(
        "🏫 *Mode & Timing*\n\n📌 *Mode:* Online / Offline / Hybrid\n🕐 *Timing:* 10:00 AM – 5:00 PM\n📅 *Days:* Monday – Saturday\n📍 *Offline Location:* Near Disha College, Kota, Raipur, C.G.\n\n💡 Online students get same live training via Zoom/Meet!",
        ["⬅️ Back to Internship"]
      );
    }

    if (o.includes("certificate") || o.includes("placement")) {
      return addBot(
        "📜 *Certificate & Placement*\n\n✅ *Certificate:* Yes! Industry-recognized certificate after completion.\n\n💼 *Placement Assistance:*\n• Resume building & mock interviews\n• Portfolio development\n• Job referrals to partner companies\n• Interview preparation sessions\n\n📌 *Eligibility:* Anyone can apply! (12th pass / Graduate / Working Professional)\n\n⚠️ *Note:* Stipend is not provided during internship, but experience & placement support is invaluable!",
        ["⬅️ Back to Internship"]
      );
    }

    if (o.includes("apply")) {
      const courseName =
        COURSES.find((c) => o.includes(c.title.toLowerCase()))?.title ||
        "Internship Program";
      window.open(
        wa(
          `Hi YashviTech! I want to apply for ${courseName} Internship.\n\nName:\nCourse: ${courseName}\nPhone:\nCity:`
        ),
        "_blank"
      );
      return addBot(
        "📝 *Application Process:*\n\n1️⃣ Share details on WhatsApp\n2️⃣ Team calls within 24 hours\n3️⃣ Choose batch (Online/Offline)\n4️⃣ Start learning with live projects! 🚀\n\nOpening WhatsApp for you…",
        ["📚 View All Courses", "⬅️ Back to Internship"]
      );
    }

    if (o.includes("back to internship")) {
      return addBot("Sure! What else would you like to know?", [
        "📚 View All Courses",
        "💰 Fees & Duration",
        "🏫 Mode & Timing",
        "📜 Certificate & Placement",
        "📝 Apply Now",
        "⬅️ Back to Home",
      ]);
    }

    /* ── CONTACT SUB ── */
    if (o.includes("whatsapp") || o.includes("get free quote")) {
      const msg = o.includes("quote")
        ? "Hi YashviTech! I need a free quote for my project."
        : "Hi YashviTech! I want to know more.";
      window.open(wa(msg), "_blank");
      return addBot("Opening WhatsApp… 💬\nOur team will reply shortly!", [
        "⬅️ Back to Home",
      ]);
    }

    if (o.includes("directions") || o.includes("map")) {
      window.open(
        "https://www.google.com/maps/place/YashviTech+IT+Solution/@21.251595,81.610679,2108m/",
        "_blank"
      );
      return addBot(
        "📍 Opening Google Maps…\n\n*YashviTech IT Solution*\nNear Disha College, Kota, Raipur, C.G.\n🕐 10 AM – 6 PM (Mon-Sat)",
        ["⬅️ Back to Home"]
      );
    }

    /* ── BACK TO HOME ── */
    if (o.includes("back to home") || o.includes("back")) {
      return addBot("Sure! How can we help you? 😊", HOME_OPTIONS);
    }

    /* ── FALLBACK ── */
    return addBot(
      "Thanks for your message! 😊\n\nPlease choose an option below or chat on WhatsApp for instant help!",
      HOME_OPTIONS
    );
  };

  /* Reset */
  const resetChat = () => {
    setMessages([
      {
        type: "bot",
        text: "Hi! 👋 Welcome to *YashviTech IT Solution Pvt. Ltd.*\n\nWe are a Software Development & Training company based in Raipur, C.G.\n\nHow can we help you today?",
      },
    ]);
    setOptions(HOME_OPTIONS);
  };

  /* Bold text formatter */
  const formatText = (text) =>
    text.split("\n").map((line, li, arr) => {
      const parts = line.split(/(\*[^*]+\*)/g);
      return (
        <span key={li}>
          {parts.map((part, pi) =>
            part.startsWith("*") && part.endsWith("*") ? (
              <strong key={pi}>{part.slice(1, -1)}</strong>
            ) : (
              <span key={pi}>{part}</span>
            )
          )}
          {li < arr.length - 1 && <br />}
        </span>
      );
    });

  /* RENDER */
  return (
    <>
      {/* FAB (Floating Action Button) */}
      <button
        className={`chatbot-fab ${open ? "chatbot-fab-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Open chat"
      >
        {open ? (
          <span className="chatbot-fab-x">×</span>
        ) : (
          <>
            <img src={botIcon} alt="YashviTech Chat Logo" className="chatbot-fab-logo" />
            <span className="chatbot-fab-pulse"></span>
            <span className="chatbot-fab-badge">1</span>
          </>
        )}
      </button>

      {/* Window */}
      {open && (
        <div className="chatbot-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-left">
              <div className="chatbot-avatar">
                <img src={botIcon} alt="YashviTech Logo" className="chatbot-avatar-img" />
              </div>
              <div className="chatbot-header-info">
                <div className="chatbot-name">YashviTech IT Solution</div>
                <div className="chatbot-status">
                  <span className="chatbot-status-dot"></span>
                  Online • Replies instantly
                </div>
              </div>
            </div>
            <div className="chatbot-header-actions">
              <button type="button" onClick={resetChat} title="Reset chat">
                ↺
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                title="Close"
              >
                ×
              </button>
            </div>
          </div>

          {/* Chat Body */}
          <div className="chatbot-body" ref={bodyRef}>
            {messages.map((msg, i) => (
              <div key={i} className={`chatbot-row ${msg.type}`}>
                {msg.type === "bot" && (
                  <div className="chatbot-mini-avatar-container">
                    <img src={botIcon} alt="Bot Icon" className="chatbot-mini-avatar-img" />
                  </div>
                )}
                <div className={`chatbot-bubble ${msg.type}`}>
                  {formatText(msg.text)}
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="chatbot-row bot">
                <div className="chatbot-mini-avatar-container">
                  <img src={botIcon} alt="Bot Icon" className="chatbot-mini-avatar-img" />
                </div>
                <div className="chatbot-bubble bot typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
          </div>

          {/* Options Menu */}
          {options.length > 0 && !isTyping && (
            <div className="chatbot-options-bar">
              {options.map((opt, i) => (
                <button
                  key={i}
                  type="button"
                  className={`chatbot-option-btn ${
                    opt.includes("Back") ? "back" : ""
                  }`}
                  onClick={() => handleOption(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="chatbot-footer">
            <span>
              Powered by <strong>YashviTech</strong>
            </span>
            <a
              href={wa("Hi YashviTech! I want to know more.")}
              target="_blank"
              rel="noopener noreferrer"
              className="chatbot-wa-link"
            >
              💬 WhatsApp
            </a>
          </div>
        </div>
      )}
    </>
  );
}